import React, { useCallback, useEffect, createContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_WARNING } from 'ui-components/services/notification-service';

import { useExternalInventoryConnectionsContext } from './external-inventory-connections-context';
import messages from './messages';
import { useEnableExternalInventory } from '../../feature-flags/hooks/use-enable-external-inventory';
import { useQuickSearchTerm } from '../../hooks/routing/use-quick-search-term';
import { getExternalInventory } from '../../store/inventory/external-inventory.api';
import { getInventory } from '../../store/inventory/inventory.api';
import { getProducts } from '../../store/products/products.api';

export const QuickSearchContext = createContext();

const MIN_EXTERNAL_INVENTORY_SEARCH_LENGTH = 3;

/**
 * SearchContextProvider component that provides quick search functionality
 * for products, inventory, and external inventory sources.
 * @param {object} props - Component props
 * @param {React.ReactNode} [props.children] - Child components to render
 * @returns {React.ReactElement} The provider component
 */
export const SearchContextProvider = ({ children = null }) => {
  const location = useLocation();
  const history = useHistory();
  const searchTerm = useQuickSearchTerm() || '';
  const [quickSearch, setQuickSearch] = useAsyncSafeState({});
  const isExternalInventoryFFEnabled = useEnableExternalInventory();
  const { connectionIds } = useExternalInventoryConnectionsContext();

  useEffect(() => {
    if (!searchTerm) {
      return;
    }

    const productQuickSearch = getProducts({ searchTerm });
    const inventoryQuickSearch = getInventory({ searchTerm });

    const isExternalInventorySearchValid = searchTerm.length >= MIN_EXTERNAL_INVENTORY_SEARCH_LENGTH;

    if (isExternalInventoryFFEnabled && !isExternalInventorySearchValid && connectionIds?.length > 0) {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_WARNING,
        title: <FormattedMessage {...messages.externalInventorySearchTooShortTitle} />,
        content: <FormattedMessage {...messages.externalInventorySearchTooShortContent} />,
      });
    }

    const externalInventoryQuickSearch =
      isExternalInventoryFFEnabled && isExternalInventorySearchValid && connectionIds?.length > 0
        ? getExternalInventory({ searchTerm, connectionIds })
        : null;

    const promises = [productQuickSearch.promise, inventoryQuickSearch.promise];

    if (externalInventoryQuickSearch) {
      promises.push(externalInventoryQuickSearch.promise);
    }

    Promise.all(promises)
      .then((results) => {
        const { totalCount: productsCount = 0 } = results[0] ?? {};
        const { totalCount: inventoryCount = 0 } = results[1] ?? {};
        const { totalCount: externalInventoryCount = 0 } = results[2] ?? {};

        setQuickSearch({ productsCount, inventoryCount, externalInventoryCount });
      })
      .catch(() => {
        setQuickSearch({});
      });

    return () => {
      productQuickSearch?.cancel();
      inventoryQuickSearch?.cancel();
      externalInventoryQuickSearch?.cancel();
    };
  }, [connectionIds, searchTerm, setQuickSearch, isExternalInventoryFFEnabled]);

  const clearQuickSearch = useCallback(() => {
    history.push(location.pathname);
    setQuickSearch({});
  }, [history, location.pathname, setQuickSearch]);
  const onSubmitQuickSearch = useCallback(
    (searchQuery) => {
      const search = encodeURIComponent(searchQuery);
      history.push(`${location.pathname}?quickSearch=${search}`);
    },
    [history, location.pathname],
  );

  const value = useMemo(
    () => ({
      quickSearch,
      clearQuickSearch,
      onSubmitQuickSearch,
    }),
    [quickSearch, clearQuickSearch, onSubmitQuickSearch],
  );

  return <QuickSearchContext.Provider value={value}>{children}</QuickSearchContext.Provider>;
};
